import { useEffect } from "react";

/**
 * Lightweight per-route SEO hook — updates document.title, meta description,
 * and Open Graph / Twitter card tags. Avoids pulling in react-helmet.
 */
const ensure = (selector, create) => {
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = create();
    document.head.appendChild(tag);
  }
  return tag;
};

const setMetaName = (name, content) => {
  if (!content) return;
  const tag = ensure(`meta[name="${name}"]`, () => {
    const t = document.createElement("meta");
    t.setAttribute("name", name);
    return t;
  });
  tag.setAttribute("content", content);
};

const setMetaProp = (property, content) => {
  if (!content) return;
  const tag = ensure(`meta[property="${property}"]`, () => {
    const t = document.createElement("meta");
    t.setAttribute("property", property);
    return t;
  });
  tag.setAttribute("content", content);
};

const removeMetaProp = (property) => {
  const tag = document.querySelector(`meta[property="${property}"]`);
  if (tag) tag.remove();
};

const removeMetaName = (name) => {
  const tag = document.querySelector(`meta[name="${name}"]`);
  if (tag) tag.remove();
};

export default function useSeo({ title, description, image, type = "website" }) {
  useEffect(() => {
    if (title) document.title = title;
    setMetaName("description", description);

    const url = typeof window !== "undefined" ? window.location.href : "";

    setMetaProp("og:title", title);
    setMetaProp("og:description", description);
    setMetaProp("og:type", type);
    setMetaProp("og:url", url);
    setMetaProp("og:site_name", "Tri-Axis XYZ CAD Solutions");
    if (image) {
      setMetaProp("og:image", image);
      setMetaProp("og:image:width", "1200");
      setMetaProp("og:image:height", "630");
    } else {
      // Clear stale image tags when navigating to a page that doesn't define one
      removeMetaProp("og:image");
      removeMetaProp("og:image:width");
      removeMetaProp("og:image:height");
    }

    setMetaName("twitter:card", image ? "summary_large_image" : "summary");
    setMetaName("twitter:title", title);
    setMetaName("twitter:description", description);
    if (image) {
      setMetaName("twitter:image", image);
    } else {
      removeMetaName("twitter:image");
    }
    // setMetaName, setMetaProp, removeMetaName, removeMetaProp are module-level pure
    // helpers — their references never change, so they are implicit stable deps.
  }, [title, description, image, type]);
}
